import { memo } from 'react';
import { useTheme } from 'styled-components';

import { useGetCounterpartiesQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import type { DealFloatingRateLoanData } from '@src/types/OTC';
import { formatOtcDealDateTimeInTimezone, getOtcDealSpreadDisplayValue } from '@src/utils/OTC/otcDealDisplayUtils';
import {
  Divider,
  EMPTY_OBJECT,
  HStack,
  InlineFormattedNumber,
  isFloatingRateLoanSecurity,
  Text,
  useSecuritiesContext,
  VStack,
} from '@talos/kyoko';
import { Wrapper } from '../OMSCard/styles';
import { DealCardHeader } from './DealCardHeader';
import { resolveDealSecurityForCard } from './resolveDealSecurityForCard';
import { useOtcDealSettlementTimezone } from './useOtcDealSettlementTimezone';

interface DealCardFloatingRateLoanProps {
  deal: DealFloatingRateLoanData;
}

export const DealCardFloatingRateLoan = memo(function DealCardFloatingRateLoan({
  deal,
}: DealCardFloatingRateLoanProps) {
  const theme = useTheme();
  const { subAccountsByName } = useSubAccounts();
  const { data: counterpartiesByName } = useGetCounterpartiesQuery(EMPTY_OBJECT);
  const { securitiesBySymbol } = useSecuritiesContext();
  const settlementTimezone = useOtcDealSettlementTimezone(deal);
  const security = resolveDealSecurityForCard(securitiesBySymbol, deal);
  const borrowedAsset =
    (isFloatingRateLoanSecurity(security) && security.ProductDetails.BorrowedAsset) ||
    deal.DealInfo?.EnteredQuantityAsset;

  const side = deal.DealInfo?.Side;
  const loanAmount = deal.DealInfo?.LoanAmount;
  const maturityTime = deal.DealInfo?.ExpiryTime;
  const rateNameDisplay =
    isFloatingRateLoanSecurity(security) && security.ProductDetails.RateSource?.trim()
      ? security.ProductDetails.RateSource.trim()
      : '—';
  const spreadDisplayValue = getOtcDealSpreadDisplayValue(deal);
  const spreadDisplay = spreadDisplayValue != null ? `${spreadDisplayValue.value} bps` : '—';

  return (
    <Wrapper>
      <VStack gap="spacing8" alignItems="stretch" p="spacing4">
        <DealCardHeader deal={deal} />

        <Divider color={theme.colors.gray['000']} />

        <VStack gap="spacing4" alignItems="stretch">
          <HStack justifyContent="space-between" alignItems="center">
            <Text size="fontSizeMd" weight="fontWeightMedium">
              {side || 'N/A'}
            </Text>
            <VStack gap="spacing2" alignItems="flex-end">
              <InlineFormattedNumber number={loanAmount} currency={borrowedAsset} />
            </VStack>
          </HStack>
          <HStack justifyContent="space-between">
            <Text size="fontSizeSmall" color="colorTextSecondary">
              Rate
            </Text>
            <Text size="fontSizeSmall" weight="fontWeightMedium">
              {rateNameDisplay}
            </Text>
          </HStack>
          <HStack justifyContent="space-between">
            <Text size="fontSizeSmall" color="colorTextSecondary">
              Spread
            </Text>
            <Text size="fontSizeSmall" weight="fontWeightMedium">
              {spreadDisplay}
            </Text>
          </HStack>

          <HStack justifyContent="space-between">
            <Text size="fontSizeSmall" color="colorTextSecondary">
              Counterparty
            </Text>
            <Text size="fontSizeSmall" weight="fontWeightMedium">
              {deal.Counterparty
                ? (counterpartiesByName?.get(deal.Counterparty)?.DisplayName ?? deal.Counterparty)
                : deal.Counterparty}
            </Text>
          </HStack>

          <HStack justifyContent="space-between">
            <Text size="fontSizeSmall" color="colorTextSecondary">
              Sub Account
            </Text>
            <Text size="fontSizeSmall" weight="fontWeightMedium">
              {deal.SubAccount
                ? (subAccountsByName.get(deal.SubAccount)?.DisplayName ?? deal.SubAccount)
                : deal.SubAccount}
            </Text>
          </HStack>

          <HStack justifyContent="space-between">
            <Text size="fontSizeSmall" color="colorTextSecondary">
              Maturity Date
            </Text>
            <Text size="fontSizeSmall" weight="fontWeightMedium">
              {formatOtcDealDateTimeInTimezone(maturityTime, settlementTimezone)}
            </Text>
          </HStack>
        </VStack>
      </VStack>
    </Wrapper>
  );
});
